/**
 * Bevel modifier — non-destructive counterpart to the edit-mode Bevel op.
 *
 * Reuses the same Rust/WASM `bevelGeometry` kernel that powers the
 * edit-mode operator. The only modifier-specific work is resolving which
 * edges to bevel from the `limitMethod` (see `bevelSelection.ts`).
 *
 * Requires `geo.topology` to be present — bevel is polygon-native and has
 * no meaningful triangle-only fallback. Preceding retopologizers (subsurf,
 * skin) null out `topology`; when that happens we pass through unchanged.
 */

import type { BlendModifier } from "../../blend-reader/types/BlendModifier.ts";
import { dlog } from "../../debug/debugLogger.ts";
import { buildSharedVertexIndices } from "../../modeling/loopExpand.ts";
import { identityProvenance } from "../../modeling/provenance.ts";
import { bevelGeometry } from "../../wasm/editOps.ts";
import { resolveBevelEdgeSet } from "./bevelSelection.ts";
import { projectVertexGroupThroughProvenance } from "./helpers.ts";
import type { ModifierGeometry } from "./modifierTypes.ts";

export function bevelCore(
  geo: ModifierGeometry,
  mod: Extract<BlendModifier, { kind: "bevel" }>,
): ModifierGeometry {
  const vertCount = geo.positions.length / 3;

  // No polygon topology → cannot bevel. Surface a warn so the UI chip path
  // (ModifiersPanel) and devs have telemetry; the user sees the modifier's
  // amber chip rather than a broken viewport.
  if (!geo.topology) {
    dlog.warn(
      "js",
      "bevel.skip",
      "bevel requires polygon topology; preceding retopologizing modifier removed it",
    );
    return geo;
  }

  // Sub-meaningful widths are no-ops.
  if (!(mod.offset > 0) || mod.segments < 1) {
    return { ...geo, provenance: identityProvenance(vertCount) };
  }

  // "vertices" affect mode is reserved; no Rust entry point yet.
  if (mod.affect === "vertices") {
    return { ...geo, provenance: identityProvenance(vertCount) };
  }

  const vgAllowed = projectVertexGroupThroughProvenance(
    mod.vertexGroupIndices,
    geo.provenance,
  );

  const selectedEdges = resolveBevelEdgeSet(
    geo.positions,
    geo.topology,
    mod,
    vgAllowed,
  );

  if (selectedEdges.size === 0) {
    return { ...geo, provenance: identityProvenance(vertCount) };
  }

  const op = bevelGeometry(
    geo.positions,
    geo.topology,
    new Set<number>(),
    selectedEdges,
    mod.offset,
    Math.max(1, Math.min(16, mod.segments | 0)),
    Math.max(0, Math.min(1, mod.profile)),
    mod.clampOverlap,
    mod.markSharp,
    mod.markSeam,
  );

  // Kernel declined (degenerate selection, all overlaps clamped to zero, etc).
  if (!op) {
    return { ...geo, provenance: identityProvenance(vertCount) };
  }

  // Bevel is polygon-preserving in output — propagate the topology so
  // downstream modifiers (and a potential subsequent bevel) can read it.
  return {
    positions: op.vertices,
    indices: buildSharedVertexIndices(op.indices, op.topology),
    normals: null,
    uvs: null,
    morphArrays: [],
    skinIndex: null,
    skinWeight: null,
    sculptDisplacement: null,
    groups: geo.groups,
    morphTargetsRelative: geo.morphTargetsRelative,
    morphNames: geo.morphNames,
    edges: op.topology.edges,
    edgeFlags: op.topology.edgeFlags,
    edgeCreases: op.topology.edgeCreases ?? null,
    skinRadii: null,
    topology: op.topology,
    faceSources: op.faceSources,
    faceOwnerVertices: op.faceOwnerVertices,
    provenance: op.provenance,
  };
}
